import { Box, Container, Flex, theme } from '@energiebespaarders/symbols';
import { Heading } from '@energiebespaarders/symbols/helpers';
import { useIsMobile, useIsSmallMobile } from '@energiebespaarders/symbols/hooks';
import { mediaMax } from '@energiebespaarders/symbols/styles/breakpoints';
import { Image } from 'react-datocms';
import styled from 'styled-components';
import type { getLongReadOrProductBySlug_allProductPages_hero } from '~/types/generated/cms/getLongReadOrProductBySlug';
import TypedStructuredText from '../DatoCms/TypedStructuredText';
import DatoButton from './DatoButton';

interface Props {
  block: getLongReadOrProductBySlug_allProductPages_hero;
}

const colorContainerHeight = 275;

const HeroBg = styled(Flex)`
  background-color: ${theme.colors.green};
  color: white;
  height: ${colorContainerHeight}px;
`;

const ProductHeroContainer = styled(Flex)<{ $isSmallMobile: boolean; $isMobile: boolean }>`
  flex-direction: column;
  position: relative;
  width: 100%;
  margin-bottom: 30px;
`;

const CutoutContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 50%;
  height: 100%;
`;

const CutoutContentContainer = styled.div`
  height: 100%;
  position: relative;
`;

const CutoutImage = styled(Image)<{ $width: number }>`
  clip-path: url(#productHeroClipPath);
  width: ${x => `${x.$width}px`} !important;
  z-index: 1;
`;

const ImageContainer = styled(Box)`
  width: 100%;
  height: 100%;
  position: absolute;
  right: 0px;
  top: 0px;
`;

const StyledHeading = styled(Heading)`
  z-index: 2;

  ${mediaMax.md} {
    font-size: ${x => x.theme.type.scale[3]};
  }
`;

const ProductHero: React.FC<Props> = ({ block }) => {
  const isSmallMobile = useIsSmallMobile();
  const isMobile = useIsMobile();

  const svgSize = isSmallMobile ? 300 : isMobile ? 525 : 700;
  const scaleX = svgSize / 813;
  const scaleY = svgSize / 719;

  return isSmallMobile || isMobile ? (
    <Box>
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <Image
        data={block.image!.mobileResponsiveImage!}
        layout="responsive"
        style={{ height: 180, borderBottom: `3px solid ${theme.colors.green}` }}
        lazyLoad
        objectFit="cover"
        objectPosition={isMobile ? 'top' : 'center'}
      />
      <Container size="lg" pt={4} pb={10}>
        <Flex flexDirection="column" px={2}>
          <StyledHeading heading="h1">{block.title}</StyledHeading>
          <TypedStructuredText data={block.text[0]?.structuredText} />

          {block.buttons.map(b => (
            <DatoButton key={b.id} block={b} />
          ))}
        </Flex>
      </Container>
    </Box>
  ) : (
    <ProductHeroContainer $isSmallMobile={isSmallMobile} $isMobile={isMobile}>
      <HeroBg>
        <Container size="lg">
          <Flex alignItems="flex-end" height="100%" px={[4, 4, 4, 0]}>
            <Box width={1 / 2}>
              <StyledHeading heading="h1">{block.title}</StyledHeading>
            </Box>
          </Flex>
        </Container>
      </HeroBg>

      <Container size="lg" pb={10}>
        <Flex flexDirection="column" py={4} px={[4, 4, 4, 0]}>
          <Box width={1 / 2}>
            <TypedStructuredText data={block.text[0]?.structuredText} />

            <Box mt={4}>
              {block.buttons.map(b => (
                <DatoButton key={b.id} block={b} />
              ))}
            </Box>
          </Box>
        </Flex>
      </Container>

      {!isSmallMobile && !isMobile && (
        <CutoutContainer>
          <CutoutContentContainer>
            <ImageContainer>
              <CutoutImage
                data={block.image!.responsiveImage!}
                $width={svgSize}
                objectFit="cover"
              />
            </ImageContainer>

            <svg
              width="813"
              height="719"
              viewBox="0 0 813 719"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <clipPath id="productHeroClipPath">
                <path
                  d="M608.775 0C622.955 39.2809 640.114 77.973 660.35 115.733C698.116 187.824 744.388 251.083 798.945 307.594C817.452 326.805 816.28 358.234 796.9 377.828L481.125 694.063C447.321 727.31 392.267 726.438 360.066 693.621C274.491 603.329 200.972 501.313 139.512 387.57C72.4271 262.645 26.5718 132.309 0 0H608.775Z"
                  fill="#58FF0A"
                  transform={`scale(${scaleX} ${scaleY})`}
                />
              </clipPath>
            </svg>
          </CutoutContentContainer>
        </CutoutContainer>
      )}
    </ProductHeroContainer>
  );
};

export default ProductHero;
